/**
 * App-level tenant time zone (DNKADM-0478, F-223 / F-227).
 *
 * Reads `GET /v2/admin/tenant-settings` ONCE for the authenticated shell and provides its
 * `defaultTimezone`, so every screen's calendar zone falls back location -> tenant ->
 * device ({@link useCalendarZone}) without a per-screen settings read. The GET is open to
 * `STAFF_ROLES`, so every console session may make it.
 *
 * Fail-soft: while the read is in flight, when it fails (a `403`, a network error) or when
 * the row carries no usable zone, the value is `null` and the device zone applies, exactly
 * as before this provider existed. It never blocks rendering.
 */
import { useCallback, type ReactNode } from 'react';
import { getTenantSettings } from '@/api/tenantSettings';
import { isUsableZone } from '@/components/list/listDate';
import { useResource } from '@/hooks/useResource';
import { TenantZoneContext } from './tenantZoneContextValue';

export function TenantZoneProvider({ children }: { children: ReactNode }) {
  const settings = useResource(
    useCallback(() => getTenantSettings(), []),
    [],
  );
  const zone = settings.data?.defaultTimezone;
  return (
    <TenantZoneContext.Provider value={isUsableZone(zone) ? zone : null}>
      {children}
    </TenantZoneContext.Provider>
  );
}
